import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { getUserCapabilities, type UserCapabilities } from '@field-tickets/shared';
import { useAuthStore } from '@/store/auth';

/**
 * Route-level guard that admits a user holding ANY of the listed capabilities
 * (logical OR) — the sibling of RequireCapability for routes two roles reach from
 * different grants.
 *
 * Example (#459): the invoice builder route admits managers/admins
 * (`canManageInvoices`) and Sales (`canConvertEstimate`). The backend remains the
 * real boundary and further scopes Sales to convert drafts; this only stops a
 * technician/lead_tech from URL-hopping onto the page.
 */
export function RequireAnyCapability({
  capabilities,
  children,
}: {
  capabilities: (keyof UserCapabilities)[];
  children: ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  const caps = user ? getUserCapabilities(user) : null;
  const allowed = !!caps && capabilities.some((c) => caps[c]);
  if (!allowed) {
    return <Navigate to="/no-access" replace />;
  }
  return <>{children}</>;
}
